if (typeof ProductGalleryLightbox !== 'function') {
  class ProductGalleryLightbox extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.gallery = this.closest('[data-js-product-gallery]');
      this.dialog = this.querySelector('[data-lightbox-dialog]');
      this.stage = this.querySelector('[data-lightbox-stage]');
      this.image = this.querySelector('[data-lightbox-image]');
      this.counter = this.querySelector('[data-lightbox-counter]');
      this.closeButton = this.querySelector('[data-lightbox-close]');
      this.previousButton = this.querySelector('[data-lightbox-prev]');
      this.nextButton = this.querySelector('[data-lightbox-next]');
      this.thumbnailsContainer = this.querySelector('[data-lightbox-thumbnails]');
      this.items = Array.from(this.querySelectorAll('[data-lightbox-thumbnail]'));
      this.pointers = new Map();
      this.currentIndex = 0;
      this.imageWidth = 0;
      this.imageHeight = 0;
      this.renderedWidth = 0;
      this.renderedHeight = 0;
      this.minimumScale = 1;
      this.maximumScale = 1;
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      this.dragDistance = 0;
      this.loadToken = 0;

      if (!this.gallery || !this.dialog || !this.stage || !this.image || this.items.length === 0) return;

      this.handleGalleryClickBound = this.handleGalleryClick.bind(this);
      this.handleGalleryKeydownBound = this.handleGalleryKeydown.bind(this);
      this.handleDialogClickBound = this.handleDialogClick.bind(this);
      this.handleDialogKeydownBound = this.handleDialogKeydown.bind(this);
      this.handleDialogCancelBound = this.handleDialogCancel.bind(this);
      this.handleDialogCloseBound = this.handleDialogClose.bind(this);
      this.handleStageClickBound = this.handleStageClick.bind(this);
      this.handleWheelBound = this.handleWheel.bind(this);
      this.handlePointerDownBound = this.handlePointerDown.bind(this);
      this.handlePointerMoveBound = this.handlePointerMove.bind(this);
      this.handlePointerUpBound = this.handlePointerUp.bind(this);

      this.gallery.addEventListener('click', this.handleGalleryClickBound);
      this.gallery.addEventListener('keydown', this.handleGalleryKeydownBound);
      this.dialog.addEventListener('click', this.handleDialogClickBound);
      this.dialog.addEventListener('keydown', this.handleDialogKeydownBound);
      this.dialog.addEventListener('cancel', this.handleDialogCancelBound);
      this.dialog.addEventListener('close', this.handleDialogCloseBound);
      this.stage.addEventListener('click', this.handleStageClickBound);
      this.stage.addEventListener('wheel', this.handleWheelBound, { passive: false });
      this.stage.addEventListener('pointerdown', this.handlePointerDownBound);
      this.stage.addEventListener('pointermove', this.handlePointerMoveBound);
      this.stage.addEventListener('pointerup', this.handlePointerUpBound);
      this.stage.addEventListener('pointercancel', this.handlePointerUpBound);

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.dialog.open) this.updateFit(false);
        });
        this.resizeObserver.observe(this.stage);
      }
    }

    disconnectedCallback() {
      if (!this.initialized || !this.gallery || !this.dialog || !this.stage) return;

      this.gallery.removeEventListener('click', this.handleGalleryClickBound);
      this.gallery.removeEventListener('keydown', this.handleGalleryKeydownBound);
      this.dialog.removeEventListener('click', this.handleDialogClickBound);
      this.dialog.removeEventListener('keydown', this.handleDialogKeydownBound);
      this.dialog.removeEventListener('cancel', this.handleDialogCancelBound);
      this.dialog.removeEventListener('close', this.handleDialogCloseBound);
      this.stage.removeEventListener('click', this.handleStageClickBound);
      this.stage.removeEventListener('wheel', this.handleWheelBound);
      this.stage.removeEventListener('pointerdown', this.handlePointerDownBound);
      this.stage.removeEventListener('pointermove', this.handlePointerMoveBound);
      this.stage.removeEventListener('pointerup', this.handlePointerUpBound);
      this.stage.removeEventListener('pointercancel', this.handlePointerUpBound);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      document.documentElement.classList.remove('product-gallery-lightbox-open');
    }

    handleGalleryClick(event) {
      const trigger = event.target.closest('[data-product-lightbox-open]');
      if (!trigger || !this.gallery.contains(trigger) || this.dialog.contains(trigger)) return;

      event.preventDefault();
      this.open(trigger);
    }

    handleGalleryKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      const trigger = event.target.closest('[data-product-lightbox-open]');
      if (!trigger || !this.gallery.contains(trigger) || this.dialog.contains(trigger)) return;

      event.preventDefault();
      this.open(trigger);
    }

    handleDialogClick(event) {
      if (event.target === this.dialog || event.target.closest('[data-lightbox-close]')) {
        this.close();
        return;
      }

      if (event.target.closest('[data-lightbox-prev]')) {
        this.select(this.currentIndex - 1);
        return;
      }

      if (event.target.closest('[data-lightbox-next]')) {
        this.select(this.currentIndex + 1);
        return;
      }

      const thumbnail = event.target.closest('[data-lightbox-thumbnail]');
      if (thumbnail) this.select(this.items.indexOf(thumbnail));
    }

    handleDialogKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.select(this.currentIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.select(this.currentIndex + 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.select(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.select(this.items.length - 1);
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.setScale(this.scale * 1.2);
      } else if (event.key === '-') {
        event.preventDefault();
        this.setScale(this.scale / 1.2);
      }
    }

    handleDialogCancel(event) {
      event.preventDefault();
      this.close();
    }

    handleDialogClose() {
      document.documentElement.classList.remove('product-gallery-lightbox-open');
      this.pointers.clear();
      this.stage.classList.remove('is-dragging');

      if (this.opener && this.opener.isConnected) this.opener.focus();
      this.opener = null;
    }

    open(trigger) {
      this.opener = trigger;
      const mediaId = trigger.dataset.mediaId;
      const itemIndex = this.items.findIndex((item) => item.dataset.mediaId === mediaId);

      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('product-gallery-lightbox-open');
      this.select(itemIndex >= 0 ? itemIndex : 0);

      requestAnimationFrame(() => {
        this.updateFit(true);
        this.closeButton.focus();
      });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    select(index) {
      if (index < 0 || index >= this.items.length) return;

      this.currentIndex = index;
      const item = this.items[index];
      this.items.forEach((thumbnail, thumbnailIndex) => {
        if (thumbnailIndex === index) {
          thumbnail.setAttribute('aria-current', 'true');
        } else {
          thumbnail.removeAttribute('aria-current');
        }
      });

      this.previousButton.disabled = index === 0;
      this.nextButton.disabled = index === this.items.length - 1;
      this.counter.textContent = `${index + 1} / ${this.items.length}`;
      this.stage.classList.add('is-loading');
      this.stage.classList.remove('is-ready');
      this.stage.setAttribute('aria-busy', 'true');

      const token = ++this.loadToken;
      const loader = new Image();
      this.pendingImage = loader;
      loader.decoding = 'async';
      loader.onload = () => {
        if (token !== this.loadToken) return;

        this.imageWidth = loader.naturalWidth;
        this.imageHeight = loader.naturalHeight;
        this.image.src = loader.src;
        this.image.alt = item.dataset.imageAlt || '';
        this.image.width = this.imageWidth;
        this.image.height = this.imageHeight;
        requestAnimationFrame(() => {
          if (token !== this.loadToken) return;
          this.updateFit(true);
          this.stage.classList.remove('is-loading');
          this.stage.classList.add('is-ready');
          this.stage.setAttribute('aria-busy', 'false');
        });
      };
      loader.onerror = () => {
        if (token !== this.loadToken) return;
        this.stage.classList.remove('is-loading');
        this.stage.setAttribute('aria-busy', 'false');
      };
      loader.src = item.dataset.imageUrl;

      if (!item.hidden) {
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    updateFit(forceFit) {
      if (!this.imageWidth || !this.imageHeight || !this.stage.clientWidth || !this.stage.clientHeight) return;

      const wasAtFit = Math.abs(this.scale - this.minimumScale) < 0.01;
      const horizontalGap = window.innerWidth < 768 ? 32 : 96;
      const availableWidth = Math.max(this.stage.clientWidth - horizontalGap, 1);
      const availableHeight = Math.max(this.stage.clientHeight - 32, 1);
      const fitRatio = Math.min(availableWidth / this.imageWidth, availableHeight / this.imageHeight, 1);
      this.renderedWidth = Math.max(Math.round(this.imageWidth * fitRatio), 1);
      this.renderedHeight = Math.max(Math.round(this.imageHeight * fitRatio), 1);
      this.image.style.width = `${this.renderedWidth}px`;
      this.image.style.height = `${this.renderedHeight}px`;
      this.image.style.marginLeft = `${this.renderedWidth / -2}px`;
      this.image.style.marginTop = `${this.renderedHeight / -2}px`;
      this.minimumScale = 1;
      this.maximumScale = Math.max(
        1,
        Math.min(4, this.imageWidth / this.renderedWidth, this.imageHeight / this.renderedHeight)
      );

      if (forceFit || wasAtFit) {
        this.scale = this.minimumScale;
        this.panX = 0;
        this.panY = 0;
      } else {
        this.scale = Math.min(Math.max(this.scale, this.minimumScale), this.maximumScale);
        this.clampPan();
      }

      this.renderTransform();
    }

    setScale(nextScale, clientX, clientY) {
      if (!this.imageWidth || !this.imageHeight) return;

      const clampedScale = Math.min(Math.max(nextScale, this.minimumScale), this.maximumScale);
      if (Math.abs(clampedScale - this.scale) < 0.001) return;

      if (Number.isFinite(clientX) && Number.isFinite(clientY)) {
        const rect = this.stage.getBoundingClientRect();
        const pointX = clientX - rect.left - rect.width / 2;
        const pointY = clientY - rect.top - rect.height / 2;
        const ratio = clampedScale / this.scale;
        this.panX = pointX - (pointX - this.panX) * ratio;
        this.panY = pointY - (pointY - this.panY) * ratio;
      }

      this.scale = clampedScale;
      this.clampPan();
      this.renderTransform();
    }

    clampPan() {
      const maximumPanX = Math.max((this.renderedWidth * this.scale - this.stage.clientWidth) / 2, 0);
      const maximumPanY = Math.max((this.renderedHeight * this.scale - this.stage.clientHeight) / 2, 0);
      this.panX = Math.min(Math.max(this.panX, -maximumPanX), maximumPanX);
      this.panY = Math.min(Math.max(this.panY, -maximumPanY), maximumPanY);
    }

    renderTransform() {
      this.image.style.transform = `matrix(${this.scale}, 0, 0, ${this.scale}, ${this.panX}, ${this.panY})`;
      this.stage.classList.toggle('is-zoomed', this.scale > this.minimumScale + 0.01);
    }

    handleStageClick(event) {
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }

      if (this.maximumScale <= this.minimumScale + 0.01) return;

      if (this.scale > this.minimumScale + 0.01) {
        this.panX = 0;
        this.panY = 0;
        this.setScale(this.minimumScale);
      } else {
        this.setScale(this.maximumScale, event.clientX, event.clientY);
      }
    }

    handleWheel(event) {
      event.preventDefault();
      const zoomFactor = Math.exp(event.deltaY * -0.0015);
      this.setScale(this.scale * zoomFactor, event.clientX, event.clientY);
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.stage.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.dragDistance = 0;
      this.stage.classList.add('is-dragging');

      if (this.pointers.size === 2) this.setPinchOrigin();
    }

    handlePointerMove(event) {
      const previousPointer = this.pointers.get(event.pointerId);
      if (!previousPointer) return;

      event.preventDefault();
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 1) {
        const deltaX = event.clientX - previousPointer.x;
        const deltaY = event.clientY - previousPointer.y;
        this.dragDistance += Math.abs(deltaX) + Math.abs(deltaY);

        if (this.scale > this.minimumScale + 0.01) {
          this.panX += deltaX;
          this.panY += deltaY;
          this.clampPan();
          this.renderTransform();
        }
      } else if (this.pointers.size === 2) {
        const points = Array.from(this.pointers.values());
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        const centerX = (points[0].x + points[1].x) / 2;
        const centerY = (points[0].y + points[1].y) / 2;

        if (this.pinchDistance > 0) {
          this.panX += centerX - this.pinchCenterX;
          this.panY += centerY - this.pinchCenterY;
          this.setScale(this.scale * (distance / this.pinchDistance), centerX, centerY);
        }

        this.pinchDistance = distance;
        this.pinchCenterX = centerX;
        this.pinchCenterY = centerY;
        this.dragDistance += 2;
      }
    }

    handlePointerUp(event) {
      if (!this.pointers.has(event.pointerId)) return;

      this.pointers.delete(event.pointerId);
      if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);

      if (this.dragDistance > 4) {
        this.suppressClick = true;
        window.setTimeout(() => {
          this.suppressClick = false;
        }, 250);
      }

      if (this.pointers.size === 1) {
        this.pinchDistance = 0;
      } else if (this.pointers.size === 0) {
        this.stage.classList.remove('is-dragging');
        this.pinchDistance = 0;
      }
    }

    setPinchOrigin() {
      const points = Array.from(this.pointers.values());
      this.pinchDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      this.pinchCenterX = (points[0].x + points[1].x) / 2;
      this.pinchCenterY = (points[0].y + points[1].y) / 2;
    }
  }

  if (typeof customElements.get('product-gallery-lightbox') === 'undefined') {
    customElements.define('product-gallery-lightbox', ProductGalleryLightbox);
  }
}
